import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Bell, ChevronRight, X } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import {
  useInAppUnreadAnnounce,
  type LiveBanner,
  type UnreadNotifItem,
} from '../../hooks/useInAppUnreadAnnounce';
import {
  NotificacaoDetalheSheet,
  type NotificacaoDetalhe,
} from './NotificacaoDetalheSheet';
import { buildNotificacaoDeepLink } from '../../utils/notificationDeepLink';
import { ROUTES } from '../../api/routes';
import { useResponsavelId } from '../../pages/responsavel/useResponsavelId';
import { UserAvatar } from '../ui/UserAvatar';

type UnreadCtx = {
  unreadCount: number;
  bellPulse: boolean;
  liveBanner: LiveBanner | null;
  dismissBanner: () => void;
  actionHref: string;
  openNotificacao: (n: NotificacaoDetalhe) => void;
};

const UnreadNotificationsContext = createContext<UnreadCtx>({
  unreadCount: 0,
  bellPulse: false,
  liveBanner: null,
  dismissBanner: () => {},
  actionHref: '/',
  openNotificacao: () => {},
});

export function useUnreadNotifications() {
  return useContext(UnreadNotificationsContext);
}

function resolveListHref(perfil?: string | null): string {
  const p = String(perfil || '').toUpperCase();
  if (p === 'PROFESSOR') return '/professor/comunicados';
  if (p === 'MUNICIPAL') return '/municipal/comunicados';
  if (p === 'RESPONSAVEL') return '/responsavel/notificacoes';
  return '/admin/comunicados';
}

/** Perfis com inbox via `usuario-destino`. RESP usa timeline própria na home. */
function canPollUsuarioDestino(perfil?: string | null): boolean {
  const p = String(perfil || '').toUpperCase();
  return p === 'PROFESSOR' || p === 'ESCOLA' || p === 'ADMIN' || p === 'MUNICIPAL';
}

function FloatingLiveBanner({
  banner,
  onOpen,
  onDismiss,
}: {
  banner: LiveBanner;
  onOpen: () => void;
  onDismiss: () => void;
}) {
  return (
    <div
      style={{
        position: 'fixed',
        top: 14,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 99999,
        width: 'min(460px, calc(100vw - 24px))',
        pointerEvents: 'auto',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 16px',
          borderRadius: 16,
          background: 'linear-gradient(135deg, #f0f7ff 0%, #ffffff 65%)',
          boxShadow: '0 12px 32px rgba(37, 99, 235, 0.22), 0 2px 6px rgba(0,0,0,0.04)',
          border: '1.5px solid rgba(59, 130, 246, 0.35)',
        }}
      >
        <button
          type="button"
          onClick={onOpen}
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            textAlign: 'left',
            padding: 0,
          }}
        >
          {banner.fotoUrl ? (
            <img
              src={banner.fotoUrl}
              alt=""
              style={{
                width: 44,
                height: 44,
                borderRadius: 10,
                objectFit: 'cover',
                border: '1.5px solid #bfdbfe',
                flexShrink: 0,
              }}
            />
          ) : banner.nome ? (
            <UserAvatar
              src={banner.fotoUrl}
              name={banner.nome || banner.title}
              role={banner.role || 'ESCOLA'}
              size={42}
              shape="rounded"
              style={{ flexShrink: 0 }}
            />
          ) : (
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 10,
                flexShrink: 0,
                background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 10px rgba(37, 99, 235, 0.25)',
              }}
            >
              <Bell size={18} color="#fff" />
            </div>
          )}

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: '0.64rem',
              fontWeight: 800,
              color: '#1d4ed8',
              background: '#dbeafe',
              padding: '1px 6px',
              borderRadius: 5,
              textTransform: 'uppercase',
              letterSpacing: '0.3px',
              marginBottom: 2,
            }}>
              Novo Comunicado
            </div>

            <div
              style={{
                fontSize: '0.88rem',
                fontWeight: 800,
                color: '#0f172a',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.25,
              }}
            >
              {banner.title}
            </div>

            <div
              style={{
                fontSize: '0.74rem',
                color: '#475569',
                marginTop: 1,
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {banner.body}
            </div>
          </div>

          <div style={{
            width: 26,
            height: 26,
            borderRadius: 7,
            background: '#eff6ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <ChevronRight size={15} color="#2563eb" />
          </div>
        </button>

        <button
          type="button"
          aria-label="Fechar"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          style={{
            border: 'none',
            background: '#f1f5f9',
            borderRadius: 8,
            width: 26,
            height: 26,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#64748b',
            flexShrink: 0,
          }}
        >
          <X size={13} />
        </button>
      </div>
    </div>
  );
}

type ItemComId = UnreadNotifItem & NotificacaoDetalhe;

/**
 * Aviso in-app global + abertura de detalhe (push/toast/lista → sheet, não só a lista).
 */
export function InAppNotificationWatcher({ children }: { children?: ReactNode }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const escolaId = useAuthStore((s) => s.escolaId || s.user?.escolaId);
  const perfil = useAuthStore((s) => s.perfil || s.user?.perfil);
  const responsavelId = useResponsavelId();

  const listHref = resolveListHref(perfil);
  const pollEnabled = Boolean(
    isAuthenticated &&
      user?.id &&
      escolaId &&
      canPollUsuarioDestino(perfil),
  );

  const itemsRef = useRef<ItemComId[]>([]);
  const pendingOpenIdRef = useRef<string | null>(null);
  const [detail, setDetail] = useState<NotificacaoDetalhe | null>(null);
  const [bannerItem, setBannerItem] = useState<ItemComId | null>(null);

  const openNotificacao = useCallback((n: NotificacaoDetalhe) => {
    setDetail(n);
  }, []);

  const fetchItems = useCallback(async () => {
    if (!escolaId || !user?.id) return [];
    const { data } = await api.get(
      `/escolas/${escolaId}/notificacoes-evento/usuario-destino/${user.id}`,
    );
    const list = (Array.isArray(data) ? data : []) as ItemComId[];
    itemsRef.current = list;
    return list;
  }, [escolaId, user?.id]);

  const getActionHref = useCallback(
    (newest?: UnreadNotifItem | null) => {
      const id = (newest as ItemComId | undefined)?.id;
      if (id) return buildNotificacaoDeepLink(perfil, id);
      return listHref;
    },
    [perfil, listHref],
  );

  const { unreadCount, liveBanner, bellPulse, dismissBanner } = useInAppUnreadAnnounce({
    enabled: pollEnabled,
    fetchItems,
    actionHref: listHref,
    getActionHref,
    onAnnounceItem: (item) => {
      setBannerItem((item as ItemComId) || null);
    },
  });

  const markRespLida = useCallback(
    (id: string, lidaEm?: string | null) => {
      if (!responsavelId || lidaEm) return;
      api.patch(ROUTES.mobileResponsavel.notificacaoLida(responsavelId, id)).catch(() => {});
    },
    [responsavelId],
  );

  const tryOpenById = useCallback(
    async (id: string) => {
      const cached = itemsRef.current.find((n) => n.id === id);
      if (cached) {
        openNotificacao(cached);
        markRespLida(id, cached.lidaEm);
        return;
      }

      // RESP: timeline mobile
      const perfilU = String(perfil || '').toUpperCase();
      if (perfilU === 'RESPONSAVEL' && responsavelId) {
        try {
          const { data } = await api.get(ROUTES.mobileResponsavel.timeline(responsavelId, 50));
          const list = (Array.isArray(data) ? data : []) as ItemComId[];
          itemsRef.current = list;
          const found = list.find((n) => n.id === id);
          if (found) {
            openNotificacao(found);
            markRespLida(id, found.lidaEm);
            return;
          }
        } catch { /* noop */ }
      }

      // PROF/ESCOLA: refetch usuario-destino
      if (escolaId && user?.id && canPollUsuarioDestino(perfil)) {
        try {
          const list = await fetchItems();
          const found = list.find((n) => n.id === id);
          if (found) {
            openNotificacao(found);
            return;
          }
        } catch { /* noop */ }
      }

      // Fallback mínimo com o que veio no push
      openNotificacao({
        id,
        titulo: 'Notificação',
        mensagem: 'Abra a lista de alertas para ver o conteúdo completo.',
      });
    },
    [perfil, responsavelId, user?.id, escolaId, fetchItems, openNotificacao, markRespLida],
  );

  // Deep-link ?comunicadoId= ou ?notificacaoId= → abre modal/detalhe
  useEffect(() => {
    if (!isAuthenticated) return;
    const id = searchParams.get('comunicadoId') || searchParams.get('notificacaoId');
    if (!id) return;
    pendingOpenIdRef.current = id;
    window.dispatchEvent(new CustomEvent('ac:open:comunicado:modal', { detail: { id } }));
    void tryOpenById(id);
    const next = new URLSearchParams(searchParams);
    next.delete('comunicadoId');
    next.delete('notificacaoId');
    setSearchParams(next, { replace: true });
  }, [isAuthenticated, searchParams, setSearchParams, tryOpenById]);

  // Clique no toast/push pode disparar evento com o payload completo
  useEffect(() => {
    const onOpen = (ev: Event) => {
      const detailEv = (ev as CustomEvent).detail as NotificacaoDetalhe | undefined;
      if (detailEv?.id) {
        window.dispatchEvent(new CustomEvent('ac:open:comunicado:modal', { detail: detailEv }));
        openNotificacao(detailEv);
        markRespLida(detailEv.id, detailEv.lidaEm);
        return;
      }
      const id = (detailEv as { notificacaoId?: string } | undefined)?.notificacaoId
        || (ev as CustomEvent).detail?.notificacaoId;
      if (id) {
        window.dispatchEvent(new CustomEvent('ac:open:comunicado:modal', { detail: { id } }));
        void tryOpenById(String(id));
      }
    };
    window.addEventListener('ac-open-notificacao', onOpen as EventListener);
    return () => window.removeEventListener('ac-open-notificacao', onOpen as EventListener);
  }, [openNotificacao, tryOpenById, markRespLida]);

  const value = useMemo(
    () => ({
      unreadCount,
      bellPulse,
      liveBanner,
      dismissBanner,
      actionHref: listHref,
      openNotificacao,
    }),
    [unreadCount, bellPulse, liveBanner, dismissBanner, listHref, openNotificacao],
  );

  const openBannerDetail = () => {
    dismissBanner();
    const id = bannerItem?.id || liveBanner?.notificacaoId;
    if (id) {
      window.dispatchEvent(
        new CustomEvent('ac:open:comunicado:modal', {
          detail: {
            id,
            titulo: bannerItem?.titulo || liveBanner?.title,
            corpo: bannerItem?.mensagem || liveBanner?.body,
            fotoUrl: bannerItem?.fotoUrl || liveBanner?.fotoUrl,
          },
        }),
      );
      return;
    }
    navigate(listHref);
  };

  return (
    <UnreadNotificationsContext.Provider value={value}>
      {children}
      {liveBanner && (
        <FloatingLiveBanner
          banner={liveBanner}
          onOpen={openBannerDetail}
          onDismiss={dismissBanner}
        />
      )}
      <NotificacaoDetalheSheet notificacao={detail} onClose={() => setDetail(null)} />
    </UnreadNotificationsContext.Provider>
  );
}
